/**
 * The two reads behind a Team queue's "Also today" fold: the pull requests
 * Proctor reviewed on the Team's repositories and Harper's runs. There is no
 * count-only read, and the fold's line and count need both, so they are read
 * when the queue mounts, but once per Team: the answer is cached per api and
 * Team, a remount inside FRESH_MS reuses it, reads already in flight are
 * shared, and a burst of `tickets.invalidate` events costs one re-read.
 */
import { useEffect, useState } from "react";
import type { CodeReviewWatchedPr } from "@yagni/shared";
import type { HarperRunRow } from "../workers/useWorkers.js";
import type { ReviewsApi } from "../reviews/reviewsApi.js";
import { captureUiError } from "../sentry.js";

export interface AlsoTodayData {
  prs: CodeReviewWatchedPr[];
  runs: HarperRunRow[];
}

interface Entry {
  data: AlsoTodayData;
  at: number;
  inflight: Promise<AlsoTodayData> | null;
}

/** How long a read stays good for a remount. */
export const FRESH_MS = 30_000;
/** Invalidations inside this window share one re-read. */
export const INVALIDATE_COALESCE_MS = 1_000;

const EMPTY: AlsoTodayData = { prs: [], runs: [] };
const caches = new WeakMap<ReviewsApi, Map<string, Entry>>();

function entryOf(api: ReviewsApi, deskId: string): Entry {
  let byDesk = caches.get(api);
  if (!byDesk) {
    byDesk = new Map();
    caches.set(api, byDesk);
  }
  let entry = byDesk.get(deskId);
  if (!entry) {
    entry = { data: EMPTY, at: 0, inflight: null };
    byDesk.set(deskId, entry);
  }
  return entry;
}

/** One read of both lists; a half that fails keeps what it had and is reported. */
function read(api: ReviewsApi, deskId: string): Promise<AlsoTodayData> {
  const entry = entryOf(api, deskId);
  if (entry.inflight) return entry.inflight;
  let failed = 0;
  const prs = api.listDeskPrs(deskId).then(
    (page) => page.rows,
    (err: unknown) => {
      failed += 1;
      captureUiError(err, { area: "Teams", operation: "read_also_today_reviews", deskId, severity: "warning" });
      return entry.data.prs;
    },
  );
  const runs = api.harperRuns(deskId).then(
    (page) => page.runs,
    (err: unknown) => {
      failed += 1;
      captureUiError(err, { area: "Teams", operation: "read_also_today_posts", deskId, severity: "warning" });
      return entry.data.runs;
    },
  );
  entry.inflight = Promise.all([prs, runs]).then(([nextPrs, nextRuns]) => {
    entry.data = { prs: nextPrs, runs: nextRuns };
    // A read that failed in full is never fresh: the next mount tries again.
    entry.at = failed === 2 ? 0 : Date.now();
    entry.inflight = null;
    return entry.data;
  });
  return entry.inflight;
}

export function useAlsoToday(api: ReviewsApi, deskId: string): AlsoTodayData & { reread: () => void } {
  const [data, setData] = useState<AlsoTodayData>(() => entryOf(api, deskId).data);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | null = null;
    const onInvalidate = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        entryOf(api, deskId).at = 0;
        setVersion((n) => n + 1);
      }, INVALIDATE_COALESCE_MS);
    };
    window.addEventListener("tickets.invalidate", onInvalidate);
    return () => {
      window.removeEventListener("tickets.invalidate", onInvalidate);
      if (timer) clearTimeout(timer);
    };
  }, [api, deskId]);

  useEffect(() => {
    let cancelled = false;
    const entry = entryOf(api, deskId);
    setData(entry.data);
    if (entry.at > 0 && Date.now() - entry.at < FRESH_MS && !entry.inflight) return;
    void read(api, deskId).then((next) => {
      if (!cancelled) setData(next);
    });
    return () => {
      cancelled = true;
    };
  }, [api, deskId, version]);

  return {
    ...data,
    reread: () => {
      entryOf(api, deskId).at = 0;
      setVersion((n) => n + 1);
    },
  };
}
